import axios from "axios";
import { environment } from "../../environment";

/** Hidden bot-trap input name. Must match SIGNUP_HONEYPOT_FIELD in order-handler signup_honeypot.ts. */
export const HONEYPOT_FIELD = 'company_website';

export interface PublicSignup {
    first: string;
    last: string;
    email: string;
    company: string;
    password: string;
    /** The website sign-up request this account completes, so the CRM keeps its answers. */
    submissionId: string | null;
    /** Create a new brand even though a similar name exists. */
    skipDuplicateCheck?: boolean;
    /** The Terms of Service version the user ticked (TOS_VERSION); recorded as their acceptance. */
    tosVersion: string;
    /** The hidden HoneypotField's value. Empty for people; a filled one makes the backend fake success and create nothing. */
    honeypot?: string;
}

export type SignupResult =
    | { ok: true }
    | { ok: false; reason: 'exists' | 'duplicate_brand' | 'invalid' | 'failed'; message: string };

// #region createAccountPublic
/**
 * Creates a brand and its admin user, the same call the portal's own sign-up
 * page makes, but with a typed outcome instead of a snackbar so an embedded
 * page can say exactly what happened. No auth header: a new brand needs none.
 */
export const createAccountPublic = async (s: PublicSignup): Promise<SignupResult> => {
    try {
        await axios.post(`${environment.authenticationUrl}/create`, {
            is_new_brand: true, brand: { name: s.company.trim() }, permission: 2,
            first: s.first.trim(), last: s.last.trim(), email: s.email.trim().toLowerCase(), password: s.password,
            submission_id: s.submissionId, tos_version: s.tosVersion, ...(s.skipDuplicateCheck ? { skip_duplicate_check: true } : {}),
            ...(s.honeypot ? { [HONEYPOT_FIELD]: s.honeypot } : {})
        });
        return { ok: true };
    } catch (err: any) {
        const status = err?.response?.status ?? 0;
        if (status === 406) return { ok: false, reason: 'exists', message: 'There is already a Wallmates account for this email.' };
        if (status === 409) return { ok: false, reason: 'duplicate_brand', message: 'A brand with a similar name is already on Wallmates.' };
        if (status === 400) return { ok: false, reason: 'invalid', message: 'Please check your details and try again.' };
        return { ok: false, reason: 'failed', message: status === 0 ? 'Could not reach Wallmates. Check your connection.' : 'Something went wrong creating your account. Please try again.' };
    }
};
